import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { DatePipe } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class FormateadorService {
    constructor(private datePipe: DatePipe) { }

    // Métodos de formateo de entrada
    formatInput(value: string): string {
        // Permitir números, coma y punto
        value = value.replace(/[^\d.,]/g, '');

        // Evitar múltiples comas
        const commaParts = value.split(',');
        if (commaParts.length > 2) {
            value = commaParts[0] + ',' + commaParts.slice(1).join('');
        }

        return value;
    }

    onNumberInput(control: AbstractControl): void {
        const value = this.formatInput(control.value);
        control.setValue(value, { emitEvent: false });
    }

    onPercentageBlur(control: AbstractControl): void {
        const value = control.value;
        if (value !== null && value !== '') {
            const numericValue = parseFloat(value.replace(',', '.'));
            if (!isNaN(numericValue)) {
                control.setValue(this.formatPorcentaje(numericValue), { emitEvent: false });
            }
        }
    }

    onCurrencyBlur(control: AbstractControl, monedaType: number = 0): void {
        const value = control.value;
        if (value !== null && value !== '') {
            const numericValue = this.parseCurrency(value);
            if (!isNaN(numericValue)) {
                control.setValue(
                    this.formatCurrency(numericValue, monedaType),
                    { emitEvent: false }
                );
            }
        }
    }

    // Métodos de formateo de porcentaje
    formatPorcentaje(value: number | string | null | undefined): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        const numValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
        if (isNaN(numValue)) return '';
        
        // Redondear a 2 decimales y convertir punto en coma para la visualización regional
        let formattedValue = numValue.toFixed(2).replace('.', ',');
        
        // Remover ceros decimales superfluos del final si son irrelevantes
        if (formattedValue.endsWith(',00')) {
            formattedValue = formattedValue.slice(0, -3);
        } else if (formattedValue.endsWith('0') && formattedValue.includes(',')) {
            formattedValue = formattedValue.slice(0, -1);
        }
        
        return `${formattedValue}%`;
    }

    parsePorcentaje(value: any): number | null {
        if (value === null || value === undefined || value === '') return null;
        let cleanValue = value.toString().replace('%', '').trim();
        cleanValue = cleanValue.replace(',', '.');
        const numericValue = parseFloat(cleanValue);
        return isNaN(numericValue) ? null : numericValue;
    }

    // Métodos de formateo de moneda
    formatCurrency(value: number | string | null | undefined, monedaType: number = 0): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        const numValue = typeof value === 'string' ? parseFloat(value) : parseFloat(value.toString());
        if (isNaN(numValue)) return '';

        // Formatear el número en el formato argentino
        const result = new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numValue);

        if (monedaType === 1) {
            return result.replace(/^\$\s*/, 'USD ');
        }

        return result;
    }

    parseCurrency(value: string): number | null {
        if (!value) return null;

        if (typeof value === 'number') return value;

        let cleanValue = value.replace(/[^\d.,]/g, '');

        const hasComma = cleanValue.includes(',');
        const hasDot = cleanValue.includes('.');

        if (hasComma && hasDot) {
            cleanValue = cleanValue.replace(/\.(?=\d{3})/g, '');
            cleanValue = cleanValue.replace(',', '.');           
        } else if (hasComma && !hasDot) {
            cleanValue = cleanValue.replace(',', '.');
        } else if (hasDot && !hasComma) {
            const parts = cleanValue.split('.');
            if (parts.length > 2) {
                const decimal = parts.pop();
                cleanValue = parts.join('') + '.' + decimal;
            }
        }

        const result = parseFloat(cleanValue);
        return isNaN(result) ? null : result;
    }

    // Métodos de formateo de fecha
    formatDate(value: string | Date): string {
        if (!value) return '-';
        return this.datePipe.transform(value, 'dd/MM/yyyy') || '-';
    }

    formatDateShort(value: string | Date): string {
        if (!value) return '-';
        return this.datePipe.transform(value, 'dd/MM/yy') || '-';
    }

    parseDate(dateString: string): string | null {
        if (!dateString) return null;
        const parts = dateString.split('/');
        if (parts.length !== 3) return null;
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }

    // Métodos de formateo de fecha y hora
    formatDateTime(value: string | Date): string {
        if (!value) return '-';
        return this.datePipe.transform(value, 'dd/MM/yyyy HH:mm:ss') || '-';
    }

    parseDateLocalFromISO(dateString: string): Date | null {
        if (!dateString) return null;
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    parseToDate(value: string | Date | null | undefined): Date {
        if (!value) {
            return new Date();
        }

        if (value instanceof Date) {
            return value;
        }

        const raw = value.toString().split('T')[0].trim();
        const fromIso = this.parseDateLocalFromISO(raw);
        if (fromIso && !isNaN(fromIso.getTime())) {
            return fromIso;
        }

        const isoFromLocal = this.parseDate(raw);
        if (isoFromLocal) {
            const fromLocal = this.parseDateLocalFromISO(isoFromLocal);
            if (fromLocal && !isNaN(fromLocal.getTime())) {
                return fromLocal;
            }
        }

        const fallback = new Date(value);
        return isNaN(fallback.getTime()) ? new Date() : fallback;
    }

    obtenerMes(anio: number) {
        switch (anio) {
            case 1: return 'Enero';
            case 2: return 'Febrero';
            case 3: return 'Marzo';
            case 4: return 'Abril';
            case 5: return 'Mayo';
            case 6: return 'Junio';
            case 7: return 'Julio';
            case 8: return 'Agosto';
            case 9: return 'Septiembre';
            case 10: return 'Octubre';
            case 11: return 'Noviembre';
            case 12: return 'Diciembre';
            default: return '-';
        }
    }

    normalizarTexto(texto: string): string {
        return texto
            .normalize('NFD')                // 🔹 Separa las letras de sus acentos (á → á)
            .replace(/[\u0300-\u036f]/g, '') // 🔹 Elimina los acentos y signos diacríticos
            .toLowerCase()                   // 🔹 Convierte todo a minúsculas
            .replace(/\s+/g, '_')            // 🔹 Reemplaza espacios por guiones bajos
            .replace(/%/g, '_porcentaje')    // 🔹 Reemplaza % por "_porcentaje"
            .replace(/[^\w_]/g, '');         // 🔹 Elimina caracteres especiales restantes
    }

    obtenerExtension(nombre: string): string {
        return nombre.split('.').pop();
    }

    removeExtension(nombre: string): string {
        return nombre.slice(0, nombre.lastIndexOf('.'));
    }

    async getBase64(file: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    }

    getFileNameFromUrl(url: string): string {
        // Divide la URL en partes utilizando el carácter "/"
        const parts = url.split('/');
        // El último elemento del array es el nombre del archivo
        const fileName = parts[parts.length - 1];
        return fileName;
    }

    getFileSize(size: number): string {
        const bytes = size;
        const kilobytes = bytes / 1024;
        if (kilobytes < 1024) {
            return kilobytes.toFixed(2) + ' KB';
        } else {
            const megabytes = kilobytes / 1024;
            return megabytes.toFixed(2) + ' MB';
        }
    }

    getFileTitle(file: File): string {
        return file.name + ' (' + this.getFileSize(file.size) + ')';
    }

    excelDateToJSDate(serial: number): Date {
        const utc_days = Math.floor(serial - 25569);
        const utc_value = utc_days * 86400; // segundos
        const date_info = new Date(utc_value * 1000);

        const fractional_day = serial - Math.floor(serial);
        let totalSeconds = Math.floor(86400 * fractional_day);

        const seconds = totalSeconds % 60;
        totalSeconds -= seconds;

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor(totalSeconds / 60) % 60;

        date_info.setHours(hours, minutes, seconds);

        return date_info;
    }

    parseNumeroMoneda(valor: string): number {
        if (!valor) return NaN;

        let limpio = valor.replace(/[^\d.,]/g, '');

        const ultimoPunto = limpio.lastIndexOf('.');
        const ultimaComa = limpio.lastIndexOf(',');

        const separadorDecimal =
            ultimoPunto > ultimaComa ? '.' :
            ultimaComa > ultimoPunto ? ',' :
            null;

        if (separadorDecimal) {
            const partes = limpio.split(separadorDecimal);
            limpio =
                partes[0].replace(/[.,]/g, '') + '.' + partes[1];
        } else {
            limpio = limpio.replace(/[.,]/g, '');
        }

        return Number(limpio);
    }

    parseDateToISO(date: any): string | null {
        if (!date) {
            return null;
        }

        if (typeof date === 'object' && typeof date.toISODate === 'function') {
            return date.isValid === false ? null : date.toISODate();
        }

        if (date instanceof Date) {
            if (isNaN(date.getTime())) return null;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        if (typeof date === 'string') {
            const fechaTexto = date.trim();

            if (!fechaTexto) {
                return null;
            }

            if (/^\d{4}-\d{2}-\d{2}$/.test(fechaTexto)) {
                return fechaTexto;
            }

            if (/^\d{2}\/\d{2}\/\d{4}$/.test(fechaTexto)) {
                return this.parseDate(fechaTexto);
            }

            const fechaParseada = new Date(fechaTexto);
            if (isNaN(fechaParseada.getTime())) return null;
            const year = fechaParseada.getFullYear();
            const month = String(fechaParseada.getMonth() + 1).padStart(2, '0');
            const day = String(fechaParseada.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        if (typeof date === 'object') {
            if (typeof date.toDate === 'function') {
                return this.parseDateToISO(date.toDate());
            }

            if ('_d' in date) {
                return this.parseDateToISO(date._d);
            }

            if (
                typeof date.year === 'number' &&
                typeof date.month === 'number' &&
                typeof date.day === 'number'
            ) {
                const year = String(date.year).padStart(4, '0');
                const month = String(date.month).padStart(2, '0');
                const day = String(date.day).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
        }

        return null;
    }
    
}
